import * as THREE from 'three';
import type { SceneTheme3D } from './sceneTheme3d.js';

const SEGMENTS = 48;
export const CORE_Y = 0.15;
const RADIUS_SCALE = 1.35;
const RADIUS_MARGIN = 1.1;

const RIM_VERTEX_SHADER = `
  varying vec3 vNormalView;
  varying vec3 vViewDir;
  void main() {
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vNormalView = normalize(normalMatrix * normal);
    vViewDir = normalize(-mvPosition.xyz);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const RIM_FRAGMENT_SHADER = `
  uniform vec3 rimColor;
  uniform float rimIntensity;
  varying vec3 vNormalView;
  varying vec3 vViewDir;
  void main() {
    float rim = pow(1.0 - clamp(dot(normalize(vNormalView), normalize(vViewDir)), 0.0, 1.0), 2.2);
    gl_FragColor = vec4(rimColor * rim * rimIntensity, rim);
  }
`;

/** Translucent glowing sphere anchoring the board in space, like a core/globe. */
export class CoreSphere3d {
  readonly group = new THREE.Group();
  private sphere: THREE.Mesh;
  private glow: THREE.Mesh;

  constructor() {
    const sphereGeo = new THREE.SphereGeometry(1, SEGMENTS, SEGMENTS);
    const sphereMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.5,
      roughness: 0.1,
      metalness: 0,
      transmission: 0.35,
      thickness: 1.2,
      clearcoat: 0.9,
      clearcoatRoughness: 0.12,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.sphere = new THREE.Mesh(sphereGeo, sphereMat);
    this.sphere.renderOrder = -1;

    const glowGeo = new THREE.SphereGeometry(1.04, 32, 32);
    const glowMat = new THREE.ShaderMaterial({
      uniforms: {
        rimColor: { value: new THREE.Color(0xffffff) },
        rimIntensity: { value: 1.1 },
      },
      vertexShader: RIM_VERTEX_SHADER,
      fragmentShader: RIM_FRAGMENT_SHADER,
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.glow = new THREE.Mesh(glowGeo, glowMat);
    this.glow.renderOrder = -1;

    this.group.add(this.sphere, this.glow);
  }

  build(boardSize: number, theme: SceneTheme3D): void {
    const offset = Math.max(1, (boardSize - 1) / 2);
    const radius = offset * RADIUS_SCALE + RADIUS_MARGIN;
    this.group.scale.setScalar(radius);
    this.group.position.y = CORE_Y;
    this.applyTheme(theme);
  }

  /** World-space radius of the sphere (unit geometry scaled uniformly). */
  getRadius(): number {
    return this.group.scale.x;
  }

  applyTheme(theme: SceneTheme3D): void {
    const mat = this.sphere.material as THREE.MeshPhysicalMaterial;
    mat.color.setHex(theme.coreColor);
    mat.opacity = theme.coreOpacity;
    const glowMat = this.glow.material as THREE.ShaderMaterial;
    glowMat.uniforms.rimColor!.value.setHex(theme.coreGlowColor);
  }

  dispose(): void {
    this.sphere.geometry.dispose();
    (this.sphere.material as THREE.Material).dispose();
    this.glow.geometry.dispose();
    (this.glow.material as THREE.Material).dispose();
    this.group.remove(this.sphere, this.glow);
  }
}
